import { useEffect, useState } from "react";
import { Form, Button, Row, Col, Container } from "react-bootstrap";
import {
  getAvailabilityByUserId,
  createAvailability,
  updateAvailability,
  getUserPracticeById,
} from "../../api";
import { Alert } from "react-bootstrap";
import { FaCheck, FaTimes } from "react-icons/fa";
import React from "react";

const AvailabilityManager = ({ userId }) => {
  const [availability, setAvailability] = useState(null);
  const [practices, setPractices] = useState([]); // 🔹 New state for practices
  const [selectedPractice, setSelectedPractice] = useState(""); // 🔹 New state for selected practice
  const [errors, setErrors] = useState({}); // 🔹 New state for form errors
  const [editing, setEditing] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [workingDays, setWorkingDays] = useState([
    {
      day: "Monday",
      start: "",
      end: "",
      padding: "",
      breaks: [],
      selectedPractices: [],
      notAvailable: false,
    },
    {
      day: "Tuesday",
      start: "",
      end: "",
      padding: "",
      breaks: [],
      selectedPractices: [],
      notAvailable: false,
    },
    {
      day: "Wednesday",
      start: "",
      end: "",
      padding: "",
      breaks: [],
      selectedPractices: [],
      notAvailable: false,
    },
    {
      day: "Thursday",
      start: "",
      end: "",
      padding: "",
      breaks: [],
      selectedPractices: [],
      notAvailable: false,
    },
    {
      day: "Friday",
      start: "",
      end: "",
      padding: "",
      breaks: [],
      selectedPractices: [],
      notAvailable: false,
    },
    {
      day: "Saturday",
      start: "",
      end: "",
      padding: "",
      breaks: [],
      selectedPractices: [],
      notAvailable: false,
    },
    {
      day: "Sunday",
      start: "",
      end: "",
      padding: "",
      breaks: [],
      selectedPractices: [],
      notAvailable: false,
    },
  ]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Fetch availability on mount
  useEffect(() => {
    if (!userId) return; // Wait until userId is available

    async function fetchAvailability() {
      try {
        const data = await getAvailabilityByUserId(userId);
        if (data && Array.isArray(data.workingDays)) {
          // Ensure each day has correct values including notAvailable
          const updatedWorkingDays = data.workingDays.map((day) => ({
            ...day,
            breaks: Array.isArray(day.breaks) ? day.breaks : [], // Ensure breaks is always an array
            notAvailable: day.notAvailable ?? false, // Ensure notAvailable is always a boolean
            selectedPractices: Array.isArray(day.selectedPractices)
              ? day.selectedPractices
              : [],
          }));
          setAvailability(data);
          setWorkingDays(updatedWorkingDays);
        } else {
          setAvailability(null);
        }
        const userPractice = await getUserPracticeById(userId);
        setPractices(userPractice || []); // Set practices from user data
        console.log("Practices fetched:", userPractice);
      } catch (error) {
        console.error("Error fetching availability:", error);
      }
    }
    fetchAvailability();
  }, [userId]); // Fetch only when userId is available

  useEffect(() => {
    if (availability) {
      setFromDate(availability.fromDate || "");
      setToDate(availability.toDate || "");
    }
  }, [availability]);

  const validate = () => {
    let newErrors = {};

    // Ensure date range is valid
    if (!fromDate) newErrors.fromDate = "Start date is required.";
    if (!toDate) newErrors.toDate = "End date is required.";
    if (fromDate && toDate && fromDate > toDate) {
      newErrors.toDate = "End date must be after start date.";
    }

    workingDays.forEach((day, index) => {
      if (!day.notAvailable) {
        if (!day.start)
          newErrors[index] = {
            ...newErrors[index],
            start: "Start time required.",
          };
        if (!day.end)
          newErrors[index] = { ...newErrors[index], end: "End time required." };
        if (day.start && day.end && day.start >= day.end) {
          newErrors[index] = {
            ...newErrors[index],
            end: "End must be after start.",
          };
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (index, field, value) => {
    const updatedDays = [...workingDays];
    updatedDays[index][field] = value;
    setWorkingDays(updatedDays);
  };

  // Handle adding a break dynamically
  const handleAddBreak = (index) => {
    const updatedDays = [...workingDays];
    updatedDays[index].breaks.push({ start: "", end: "" });
    setWorkingDays(updatedDays);
  };

  // Handle updating a specific break
  const handleBreakChange = (dayIndex, breakIndex, field, value) => {
    const updatedDays = [...workingDays];
    updatedDays[dayIndex].breaks[breakIndex][field] = value;
    setWorkingDays(updatedDays);
  };

  // Handle save (either add or update)
  const handleSave = async () => {
    if (!validate()) {
      console.error("Validation failed!");
      return;
    }

    // Ensure workingDays is not empty
    if (!workingDays || workingDays.length === 0) {
      alert("Please configure your working days before saving.");
      return;
    }

    // Construct request payload
    const availabilityData = {
      userId,
      fromDate,
      toDate,
      workingDays: workingDays.map((day) => ({
        ...day,
        breaks: day.breaks || [], // Ensure breaks is always an array
        selectedPractices: day.selectedPractices || [], // Ensure selectedPractices is always an array
        notAvailable: day.notAvailable ?? false, // Ensure notAvailable is always boolean
      })),
    };

    let response;
    try {
      if (!availability) {
        response = await createAvailability(availabilityData);
      } else {
        response = await updateAvailability(
          userId,
          fromDate,
          toDate,
          workingDays
        );
        console.log("workingDays:", workingDays);
      }

      if (response) {
        setSuccessAlert(true);
        setTimeout(() => setSuccessAlert(false), 3000);
        setEditing(false);

        // Fetch the updated availability
        const updatedAvailability = await getAvailabilityByUserId(userId);
        setAvailability(updatedAvailability);
      } else {
        alert("Failed to save availability.");
      }
    } catch (error) {
      console.error("Error saving availability:", error);
      alert("An error occurred while saving availability.");
    }
  };

  const handleDeleteBreak = (dayIndex, breakIndex) => {
    setWorkingDays((prevDays) => {
      const updatedDays = [...prevDays];
      updatedDays[dayIndex].breaks = updatedDays[dayIndex].breaks.filter(
        (_, i) => i !== breakIndex
      );
      return updatedDays;
    });
  };

  const handleToggleAvailability = (index, isChecked) => {
    setWorkingDays((prevDays) => {
      const updatedDays = [...prevDays];
      if (isChecked) {
        updatedDays[index] = {
          ...updatedDays[index],
          notAvailable: true,
          start: "",
          end: "",
          padding: "00:00",
          breaks: [],
        };
      } else {
        updatedDays[index] = {
          ...updatedDays[index],
          notAvailable: false,
          start: "",
          end: "",
          padding: "00:00",
          breaks: [],
        };
      }
      return updatedDays;
    });
  };

  function handleEdit() {
    setEditing(true); // ✅ Enters edit mode without showing alert
  }

  // Function to confirm closing the edit form
  function handleClose() {
    if (editing) {
      setCloseAlert(true);
    }
  }

  // User cancels closing (keeps editing active)
  function handleCancelNo() {
    setCloseAlert(false); // Hide close alert
    setEditing(true); // Keep editing mode active
  }

  // User confirms closing (exits edit mode)
  function handleCancelYes() {
    setCloseAlert(false); // Hide close alert
    setEditing(false); // Exit edit mode
  }

  return (
    <Container className="mb-1">
      {/* Edit & Close Button */}
      <Row className="mb-1">
        <Col className="text-end">
          {!availability ? (
            <Button variant="primary" onClick={() => setEditing(true)}>
              Add Availability
            </Button>
          ) : (
            <Button
              variant="link"
              className="text-primary"
              onClick={editing ? handleClose : handleEdit} // 🔹 Different handlers!
            >
              {editing ? (
                <i className="fa-solid fa-xmark fs-6 text-primary"></i> // Close (shows alert)
              ) : (
                <i className="fas fa-edit fs-6 text-primary"></i> // Edit (just enters edit mode)
              )}
            </Button>
          )}
        </Col>
      </Row>

      {/* Close Confirmation Alert */}
      {closeAlert && (
        <Alert
          variant="info"
          className="w-100 d-flex align-items-center justify-content-between mt-3 fs-6 fw-normal"
        >
          <span>
            Are you sure you want to close? Updates to availability will not be.
          </span>
          <div className="ms-auto d-flex">
            <Button
              variant="outline-primary"
              className="me-1"
              onClick={handleCancelYes}
            >
              <FaCheck /> {/* Confirm Close */}
            </Button>
            <Button
              variant="outline-secondary"
              className="me-1"
              onClick={handleCancelNo}
            >
              <FaTimes /> {/* Cancel Close */}
            </Button>
          </div>
        </Alert>
      )}

      {/* Success Alert on Save */}
      {successAlert && (
        <Alert
          variant="success"
          className="w-100 d-flex align-items-center justify-content-between mt-3 fs-6 fw-normal"
        >
          <span>Your availability update were successfull</span>
        </Alert>
      )}

      {editing ? (
        <Form>
          <Row className="mb-2 d-flex align-items-center">
            <Col md={2}>
              <Form.Label>Valid From:</Form.Label>
            </Col>
            <Col md={3}>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                isInvalid={!!errors.fromDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.fromDate}
              </Form.Control.Feedback>
            </Col>
            <Col md={2}>
              <Form.Label>To:</Form.Label>
            </Col>
            <Col md={3}>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                isInvalid={!!errors.toDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.toDate}
              </Form.Control.Feedback>
            </Col>
          </Row>
          {workingDays.map((day, index) => (
            <div key={day.day} className="mb-1 p-3 border position-relative">
              {/* Toggle Switch - "Not Available" */}
              <div className="position-absolute top-0 start-0 mt-2 ms-2">
                <Form.Check
                  type="switch"
                  id={`not-available-${index}`}
                  label="Not Available"
                  checked={day.notAvailable || false}
                  onChange={(e) =>
                    handleToggleAvailability(index, e.target.checked)
                  }
                />
              </div>
              <Row className="justify-content-center">
                <Col xs="auto">
                  <h6 className="mb-3 text-primary text-center">{day.day}</h6>
                </Col>
              </Row>

              {/* Show fields only if 'Not Available' is false */}
              {!day.notAvailable && (
                <>
                  {practices.length > 0 && (
                    <Row className="mb-2">
                      <Col md={2}>
                        <Form.Label className="fw-bold">
                          Practice(s):
                        </Form.Label>
                      </Col>
                      <Col md={10}>
                        <div className="d-flex flex-wrap gap-3">
                          {practices.map((practice) => (
                            <Form.Check
                              key={practice.practiceNumber}
                              type="checkbox"
                              label={practice.practiceName}
                              checked={day.selectedPractices?.includes(
                                practice.practiceNumber
                              )}
                              onChange={(e) => {
                                const updatedDays = [...workingDays];
                                const isChecked = e.target.checked;
                                const currentPractices =
                                  updatedDays[index].selectedPractices || [];

                                updatedDays[index].selectedPractices = isChecked
                                  ? [
                                      ...currentPractices,
                                      practice.practiceNumber,
                                    ]
                                  : currentPractices.filter(
                                      (pn) => pn !== practice.practiceNumber
                                    );

                                setWorkingDays(updatedDays);
                              }}
                            />
                          ))}
                        </div>
                      </Col>
                    </Row>
                  )}

                  {/* 🟢 Row 1: Working Hours & Padding */}
                  <Row className="mb-2 d-flex align-items-center">
                    <Col md={2}>
                      <Form.Label>Working Hours:</Form.Label>
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        type="time"
                        value={day.start}
                        onChange={(e) =>
                          handleChange(index, "start", e.target.value)
                        }
                        isInvalid={!!errors?.[index]?.start}
                      />
                    </Col>
                    <Col md="auto" className="px-2">
                      to
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        type="time"
                        value={day.end}
                        onChange={(e) =>
                          handleChange(index, "end", e.target.value)
                        }
                        isInvalid={!!errors?.[index]?.end}
                      />
                    </Col>
                    <Col md="auto" className="px-3">
                      <Form.Label>Padding:</Form.Label>
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        type="text" // 🔹 Change from "time" to "text"
                        value={day.padding}
                        onChange={(e) =>
                          handleChange(index, "padding", e.target.value)
                        }
                        placeholder="HH:mm"
                        pattern="\d{2}:\d{2}" // 🔹 Enforce HH:mm format
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9:]/g,
                            ""
                          ); // 🔹 Allow only numbers and colon
                        }}
                      />
                    </Col>
                  </Row>

                  {/* 🛑 NEW: Row for Error Messages Below Working Hours */}
                  {(errors?.[index]?.start || errors?.[index]?.end) && (
                    <Row className="mb-2">
                      <Col md={2}></Col>
                      <Col md={2} className="text-danger">
                        {errors?.[index]?.start}
                      </Col>
                      <Col md="auto"></Col>
                      <Col md={2} className="text-danger">
                        {errors?.[index]?.end}
                      </Col>
                    </Row>
                  )}

                  {/* 🔹 Row 2: First Break */}
                  {day.breaks?.length > 0 && (
                    <>
                      <Row className="mb-2 d-flex align-items-center">
                        <Col md={2}>
                          <Form.Label>Break(s):</Form.Label>
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="time"
                            value={day.breaks[0]?.start || ""}
                            onChange={(e) =>
                              handleBreakChange(
                                index,
                                0,
                                "start",
                                e.target.value
                              )
                            }
                            isInvalid={!!errors?.[index]?.[`break-${0}-start`]}
                          />
                        </Col>
                        <Col md="auto" className="px-2">
                          to
                        </Col>
                        <Col
                          md={2}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <Form.Control
                            type="time"
                            value={day.breaks[0]?.end || ""}
                            onChange={(e) =>
                              handleBreakChange(index, 0, "end", e.target.value)
                            }
                            isInvalid={!!errors?.[index]?.[`break-${0}-end`]}
                          />
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleDeleteBreak(index, 0)}
                          >
                            <i className="fa-regular fa-trash-can text-danger"></i>
                          </Button>
                        </Col>
                      </Row>

                      {/* 🛑 NEW: Row for Error Messages Below Breaks */}
                      {(errors?.[index]?.[`break-${0}-start`] ||
                        errors?.[index]?.[`break-${0}-end`]) && (
                        <Row className="mb-2">
                          <Col md={2}></Col>
                          <Col md={2} className="text-danger">
                            {errors?.[index]?.[`break-${0}-start`]}
                          </Col>
                          <Col md="auto"></Col>
                          <Col md={2} className="text-danger">
                            {errors?.[index]?.[`break-${0}-end`]}
                          </Col>
                        </Row>
                      )}

                      {/* Additional Breaks */}
                      {day.breaks.length > 1 &&
                        day.breaks.slice(1).map((breakTime, breakIndex) => (
                          <React.Fragment key={breakIndex}>
                            <Row className="mb-2 d-flex align-items-center">
                              <Col md={2}></Col>
                              <Col md={2}>
                                <Form.Control
                                  type="time"
                                  value={breakTime.start || ""}
                                  onChange={(e) =>
                                    handleBreakChange(
                                      index,
                                      breakIndex + 1,
                                      "start",
                                      e.target.value
                                    )
                                  }
                                  isInvalid={
                                    !!errors?.[index]?.[
                                      `break-${breakIndex + 1}-start`
                                    ]
                                  }
                                />
                              </Col>
                              <Col md="auto" className="px-2">
                                to
                              </Col>
                              <Col
                                md={2}
                                className="d-flex align-items-center justify-content-between"
                              >
                                <Form.Control
                                  type="time"
                                  value={breakTime.end || ""}
                                  onChange={(e) =>
                                    handleBreakChange(
                                      index,
                                      breakIndex + 1,
                                      "end",
                                      e.target.value
                                    )
                                  }
                                  isInvalid={
                                    !!errors?.[index]?.[
                                      `break-${breakIndex + 1}-end`
                                    ]
                                  }
                                />
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteBreak(index, breakIndex + 1)
                                  }
                                >
                                  <i className="fa-regular fa-trash-can text-danger"></i>
                                </Button>
                              </Col>
                            </Row>

                            {/* 🛑 NEW: Row for Error Messages Below Additional Breaks */}
                            {(errors?.[index]?.[
                              `break-${breakIndex + 1}-start`
                            ] ||
                              errors?.[index]?.[
                                `break-${breakIndex + 1}-end`
                              ]) && (
                              <Row className="mb-2">
                                <Col md={2}></Col>
                                <Col md={2} className="text-danger">
                                  {
                                    errors?.[index]?.[
                                      `break-${breakIndex + 1}-start`
                                    ]
                                  }
                                </Col>
                                <Col md="auto"></Col>
                                <Col md={2} className="text-danger">
                                  {
                                    errors?.[index]?.[
                                      `break-${breakIndex + 1}-end`
                                    ]
                                  }
                                </Col>
                              </Row>
                            )}
                          </React.Fragment>
                        ))}
                    </>
                  )}

                  {/* Add Break Button */}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleAddBreak(index)}
                  >
                    + Add Break
                  </Button>
                </>
              )}
            </div>
          ))}
          <Row className="d-flex justify-content-end">
            <Button
              variant="primary"
              className="d-flex justify-content-center mt-3 w-25 me-3"
              onClick={handleSave}
            >
              Submit
            </Button>
          </Row>
        </Form>
      ) : availability && availability.workingDays.length > 0 ? (
        <Form>
          <Row className="mb-2 d-flex align-items-center">
            <Col md={6}>
              <Form.Label className="fw-bold">Valid From:&nbsp;</Form.Label>
              <Form.Label>
                {availability.fromDate || "N/A"}&nbsp;&nbsp;
              </Form.Label>
              <Form.Label className="fw-bold">To:&nbsp;</Form.Label>
              <Form.Label>{availability.toDate || "N/A"}</Form.Label>
            </Col>
          </Row>
          <Row>
            {availability.workingDays
              .filter((day) => day.start && day.end) // Only show if working hours exist
              .map((day, index) => (
                <Col key={day.day} md={6} className="mb-3">
                  <div className="p-3 border rounded shadow-sm">
                    {/* Centered Day Heading */}
                    <Row className="justify-content-center">
                      <Col xs="auto">
                        <h6 className="mb-3 text-primary text-center">
                          {day.day}
                        </h6>
                      </Col>
                    </Row>

                    {/* Row 1: Working Hours & Padding */}
                    <Row className="mb-2 align-items-center">
                      <Col md={3} className="text-end pe-2">
                        <Form.Label className="fw-bold">
                          Working Hours:
                        </Form.Label>
                      </Col>
                      <Col md={3} className="px-1 ms-2">
                        <Form.Label>
                          {day.start} &nbsp; to &nbsp; {day.end}
                        </Form.Label>
                      </Col>
                      <Col md="auto" className="text-start pe-2">
                        <Form.Label className="fw-bold">Padding:</Form.Label>
                      </Col>
                      <Col md={2} className="px-1 ms-2">
                        <Form.Label>{day.padding}</Form.Label>
                      </Col>
                    </Row>

                    {/* Breaks Section (Only if Breaks Exist) */}
                    {day.breaks?.length > 0 && (
                      <>
                        <Row className="mb-1 align-items-center">
                          <Col md={3} className="text-end pe-2">
                            <Form.Label className="fw-bold">
                              Break(s):
                            </Form.Label>
                          </Col>
                          <Col md={3} className="px-1 ms-2">
                            <Form.Label>
                              {day.breaks[0]?.start || ""} &nbsp; to &nbsp;{" "}
                              {day.breaks[0]?.end || ""}
                            </Form.Label>
                          </Col>
                        </Row>
                        {day.breaks.length > 1 &&
                          day.breaks.slice(1).map((breakTime, breakIndex) => (
                            <Row
                              key={breakIndex}
                              className="align-items-center"
                            >
                              <Col md={3}></Col>
                              <Col md={3} className="px-1 ms-2">
                                <Form.Label>
                                  {breakTime.start || ""} &nbsp; to &nbsp;{" "}
                                  {breakTime.end || ""}
                                </Form.Label>
                              </Col>
                            </Row>
                          ))}
                      </>
                    )}
                  </div>
                </Col>
              ))}
          </Row>
        </Form>
      ) : (
        <p className="text-muted">No availability found.</p>
      )}
    </Container>
  );
};

export default AvailabilityManager;
